import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditMultiSelectField } from '../../../EditMultiSelectField';
import {
  type AllPreferredRegionsData,
  GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
  GET_ALL_PREFERRED_REGIONS,
  type InvestmentPreferencesData
} from '../queries/accountPreferencesQuery';

const UPDATE_ACCOUNT_REGIONS = gql`
  mutation UpdateAccountRegions($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        name
        preferredRegionIds {
          id
          name
          isActive
        }
      }
    }
  }
`;

interface EditPreferredRegionsDialogProps {
  accountId: string;
  initialValue: InvestmentPreferencesData;
  isOpen: boolean;
  onChange: (data: InvestmentPreferencesData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: InvestmentPreferencesData;
}

export function EditPreferredRegionsDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditPreferredRegionsDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Fetch all available regions
  const { data: allRegionsData, loading: loadingRegions } =
    useQuery<AllPreferredRegionsData>(GET_ALL_PREFERRED_REGIONS, {
      fetchPolicy: 'cache-first'
    });

  const [updateAccount] = useMutation(UPDATE_ACCOUNT_REGIONS, {
    refetchQueries: [
      {
        query: GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
        variables: { accountId }
      }
    ]
  });

  // Convert API data to options for multi-select
  const options = useMemo((): { label: string; value: string }[] => {
    if (!allRegionsData?.preferredRegions) return [];
    return allRegionsData.preferredRegions
      .filter(item => item.isActive)
      .map(item => ({
        label: item.name,
        value: item.id
      }));
  }, [allRegionsData]);

  // Get selected IDs from value
  const selectedIds = useMemo(
    (): string[] => value.preferredRegions.map(r => r.id),
    [value.preferredRegions]
  );

  // Check if dirty by comparing IDs
  const initialIds = useMemo(
    (): string[] => initialValue.preferredRegions.map(r => r.id).sort(),
    [initialValue.preferredRegions]
  );
  const currentIds = useMemo(
    (): string[] => [...selectedIds].sort(),
    [selectedIds]
  );
  const isDirty = JSON.stringify(currentIds) !== JSON.stringify(initialIds);

  const handleChange = (ids: string[]) => {
    // Convert IDs back to PreferenceItem objects
    const items = ids.map(id => {
      // Check if it's already in value
      const existing = value.preferredRegions.find(r => r.id === id);
      if (existing) return existing;
      // Otherwise find from options
      const option = options.find(o => o.value === id);
      return { id, name: option?.label ?? '' };
    });
    onChange({ ...value, preferredRegions: items });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: {
          id: accountId,
          values: {
            preferredRegionIds: selectedIds
          }
        }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save preferred regions');
      } else {
        toast.success('Preferred regions saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving preferred regions:', error);
      toast.error('Failed to save preferred regions');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Preferred Regions"
    >
      <EditMultiSelectField
        label="Preferred Regions"
        onChange={handleChange}
        options={options}
        placeholder={loadingRegions ? 'Loading...' : 'Select regions...'}
        values={selectedIds}
      />
    </EditDialog>
  );
}
